/**
 * CenterPanelRenderer — renders agent-composed dynamic views.
 *
 * The agent sends a panelConfig object via AppSync with:
 *   { layout: 'single' | 'split-horizontal' | 'split-vertical',
 *     components: [{ type, props, position }] }
 *
 * This renderer maps component types to React components from the
 * component library and renders them in the specified layout.
 *
 * When panelConfig is null, renders the default page content (children).
 */

import React, { useContext, useMemo } from 'react';
import styles from './CenterPanelRenderer.module.css';
import ErrorBoundary from '../shared/ErrorBoundary';
import UserProfileContext from '../../context/userprofilecontext';
import { getPermittedSkills, userCanSeeComponent } from '../../utils/permittedSkills';

// Component registry — maps type strings to React components.
// New components are registered here as the library grows.
import TransactionTable from './TransactionTable';
import TransactionAnalyzerPanel from './TransactionAnalyzerPanel';
import EntityCard from './EntityCard';
import ScanReviewPanel from './ScanReviewPanel';
import SchemaAdaptiveEditor from './SchemaAdaptiveEditor';
import DocumentSplitEditor from './DocumentSplitEditor';
import BulkEditPreviewDialog from './BulkEditPreviewDialog';
import TopicDetailPanel from './TopicDetailPanel';
import EntityNetworkPanel from './EntityNetworkPanel';
import GeoMapPanel from './GeoMapPanel';
import CheckImageReviewPanel from './CheckImageReviewPanel';
import IntakeChecklistPanel from './IntakeChecklistPanel';
import DocumentPanel from './DocumentPanel';

const COMPONENT_REGISTRY = {
  TransactionTable,           // HIL editing grid only — see file header.
  TransactionAnalyzerPanel,   // Agent rendering of published transaction sets — see /transaction-viewing skill.
  EntityCard,
  ScanReviewPanel,
  SchemaAdaptiveEditor,
  DocumentSplitEditor,
  BulkEditPreviewDialog,
  TopicDetailPanel,           // Phase 2 Topic Curation — agent-rendered topic detail with timeline + embedded TransactionAnalyzer.
  EntityNetworkPanel,         // ADR-002 — Livi-rendered entity ownership network (render_entity_network). Ungated like EntityCard; session-skill gated.
  GeoMapPanel,                // Phase 2 Topic Curation — agent-rendered Google Map with pins, connections, time-color gradient. See /topic-geo-map skill.
  CheckImageReviewPanel,      // HIL assist — agent-rendered unmatched check/deposit image + what was read, for confirm/correct. See render_check_image tool.
  IntakeChecklistPanel,       // Narrative intake (Learning Loop 1.5) — one checklist for a pasted overview; Save sends the ticked items. Ungated like EntityCard; session-skill gated.
  DocumentPanel,              // Livi documents — a Word/Excel file Livi created (save_document / create_document), with preview + Download. Ungated like EntityCard.
  // Future: Timeline, DiffView, ChartPanel,
  //         NarrativeEditor, EvidenceChain, PunchListPanel, FormPanel
};

// ErrorBoundary wraps every rendered component. Without it, an exception
// thrown by a registered component (e.g., SchemaAdaptiveEditor receiving
// malformed schema/data from a stale chat-history render request) bubbles
// to the React root and blanks the entire app, since AppShell does not
// wrap its children in an outer boundary the way AppLayout does.
function RenderComponent({ spec, onInteraction, permittedSkills }) {
  const Component = COMPONENT_REGISTRY[spec.type];
  if (!Component) {
    return (
      <div className={styles.unknownComponent}>
        Unknown component: {spec.type}
      </div>
    );
  }
  // Defense-in-depth for the cross-role center-panel S3 leak (backlog
  // 0bb94516). The backend GET /chat/center_panel and /chat/session_context
  // endpoints already strip components whose required skill the caller
  // lacks. If they ever slip — stale cache, bug, edge case — the frontend
  // must NOT render gated content. Refuses silently (no placeholder UI)
  // because the leaked content's existence itself can be a signal.
  if (!userCanSeeComponent(permittedSkills, spec.type)) {
    // eslint-disable-next-line no-console
    console.warn(
      `CenterPanelRenderer: refusing to render gated component "${spec.type}" — `
      + 'backend filter should have stripped it. Possible permission drift.'
    );
    return null;
  }
  return (
    <ErrorBoundary>
      <Component {...spec.props} onInteraction={onInteraction} />
    </ErrorBoundary>
  );
}

function RenderModal({ modalConfig, onInteraction, permittedSkills }) {
  if (!modalConfig) return null;
  const { components = [] } = modalConfig;
  return (
    <>
      {components.map((spec, idx) => (
        <RenderComponent
          key={`m-${idx}`}
          spec={spec}
          onInteraction={onInteraction}
          permittedSkills={permittedSkills}
        />
      ))}
    </>
  );
}

function CenterPanelRenderer({ panelConfig, modalConfig, children, onInteraction }) {
  // Resolve permitted skills once per render via UserProfileContext. See
  // utils/permittedSkills.js for the JS-side mirror of agent/skills.py
  // (kept in sync manually — small surface, security-critical).
  const { userProfile } = useContext(UserProfileContext) || {};
  const permittedSkills = useMemo(() => getPermittedSkills(userProfile), [userProfile]);

  const modalOverlay = (
    <RenderModal
      modalConfig={modalConfig}
      onInteraction={onInteraction}
      permittedSkills={permittedSkills}
    />
  );

  // If no agent config, render the default page content (plus any modal overlay)
  if (!panelConfig) {
    return (
      <>
        {children}
        {modalOverlay}
      </>
    );
  }

  const { layout = 'single', components = [] } = panelConfig;

  if (layout === 'single') {
    return (
      <div className={styles.singleLayout}>
        {components.map((spec, idx) => (
          <RenderComponent key={idx} spec={spec} onInteraction={onInteraction} permittedSkills={permittedSkills} />
        ))}
        {modalOverlay}
      </div>
    );
  }

  if (layout === 'split-horizontal') {
    const top = components.filter(c => c.position === 'top' || !c.position);
    const bottom = components.filter(c => c.position === 'bottom');
    return (
      <div className={styles.splitHorizontal}>
        <div className={styles.splitPane}>
          {top.map((spec, idx) => (
            <RenderComponent key={idx} spec={spec} onInteraction={onInteraction} permittedSkills={permittedSkills} />
          ))}
        </div>
        <div className={styles.splitPane}>
          {bottom.map((spec, idx) => (
            <RenderComponent key={idx} spec={spec} onInteraction={onInteraction} permittedSkills={permittedSkills} />
          ))}
        </div>
        {modalOverlay}
      </div>
    );
  }

  if (layout === 'split-vertical') {
    const left = components.filter(c => c.position === 'left' || !c.position);
    const right = components.filter(c => c.position === 'right');
    return (
      <div className={styles.splitVertical}>
        <div className={styles.splitPane}>
          {left.map((spec, idx) => (
            <RenderComponent key={idx} spec={spec} onInteraction={onInteraction} permittedSkills={permittedSkills} />
          ))}
        </div>
        <div className={styles.splitPane}>
          {right.map((spec, idx) => (
            <RenderComponent key={idx} spec={spec} onInteraction={onInteraction} permittedSkills={permittedSkills} />
          ))}
        </div>
        {modalOverlay}
      </div>
    );
  }

  // Fallback: render all components in a single layout
  return (
    <div className={styles.singleLayout}>
      {components.map((spec, idx) => (
        <RenderComponent key={idx} spec={spec} onInteraction={onInteraction} permittedSkills={permittedSkills} />
      ))}
      {modalOverlay}
    </div>
  );
}

export default CenterPanelRenderer;
